import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Star, TrendingUp } from "lucide-react";
import { PLATFORMS, TABS } from "@/lib/sideHustles";

const MAX = 3;

export default function SideHustlesAdmin() {
  const [featured, setFeatured] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/side-hustles/featured").then((r) => setFeatured(r.data.featured || [])).catch(() => {});
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/side-hustles/featured", { featured: next });
      setFeatured(data.featured || []);
      toast.success("Featured platforms updated");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const toggle = (name) => {
    if (featured.includes(name)) {
      save(featured.filter((n) => n !== name));
    } else {
      if (featured.length >= MAX) {
        toast.error(`You can feature at most ${MAX} platforms at a time. Turn one off first.`);
        return;
      }
      save([...featured, name]);
    }
  };

  const byTab = useMemo(() => TABS.map((t) => ({
    ...t, items: PLATFORMS.filter((p) => p.tab === t.key),
  })), []);

  return (
    <div data-testid="admin-sidehustles">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5 text-[#00F0FF]" /> Side Hustles — Featured this Month</h3>
          <p className="text-sm text-[#94A3B8] mt-1">Toggle up to {MAX} platforms to show a subtle gold "Featured this Month" ribbon on the public page. Default is off.</p>
        </div>
        <span className={`font-mono-tech text-xs px-3 py-1.5 rounded-full border ${featured.length >= MAX ? "border-[#F59E0B]/40 text-[#fbbf24] bg-[#F59E0B]/10" : "border-white/15 text-[#94A3B8]"}`} data-testid="featured-count">
          {featured.length} / {MAX} featured
        </span>
      </div>

      <div className="space-y-6">
        {byTab.map((t) => (
          <div key={t.key}>
            <p className="font-mono-tech text-[11px] uppercase tracking-widest text-[#64748B] mb-2">{t.label}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {t.items.map((p) => {
                const on = featured.includes(p.name);
                return (
                  <button key={p.name} onClick={() => toggle(p.name)} disabled={saving}
                    data-testid={`feature-toggle-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`text-left rounded-lg p-3 border transition flex items-center justify-between gap-3 disabled:opacity-60 ${on ? "border-[#F59E0B]/50 bg-[#F59E0B]/10" : "border-white/10 bg-[#0B0F19] hover:border-[#00F0FF]/40"}`}>
                    <div className="min-w-0">
                      <p className="font-display font-bold text-sm truncate">{p.name}</p>
                      <p className="text-xs text-[#64748B] truncate">{p.pay}</p>
                    </div>
                    <span className={`shrink-0 inline-flex items-center gap-1 text-[10px] font-mono-tech uppercase px-2 py-1 rounded-full border ${on ? "border-[#F59E0B]/40 text-[#fbbf24]" : "border-white/15 text-[#64748B]"}`}>
                      <Star className={`w-3 h-3 ${on ? "fill-[#fbbf24]" : ""}`} /> {on ? "Featured" : "Off"}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
